/**
 * wisdomResponseShaper.js
 *
 * Resolves the wisdom tone — the final pastoral character of the response.
 * Distinct from mood (which drives animation/alerts) and from conversational
 * mode (which drives length/structure).
 *
 * Wisdom tone is the "pastoral voice" of the response: who Kevia IS in this moment.
 *
 * Tones:
 *   consolador        — grief, acute pain, loss
 *   esperanzador      — discouragement, fear, loneliness
 *   contemplativo     — spiritual reflection, meditation, mystery
 *   reflexivo         — sermon prep, deep thinking, doctrinal exploration
 *   pastoral          — default, balanced, caring without being heavy
 *   exhortativo_suave — when gentle encouragement or nudge is warranted
 *   celebrativo       — genuine good news, growth, celebration
 *   practico          — planning, operational, analytical, task-driven
 */

const WISDOM_TONE_INSTRUCTIONS = {
  consolador:
    "Consuela antes de explicar. Menos palabras, más presencia. No resuelvas demasiado rápido.",
  esperanzador:
    "Trae esperanza con raíces reales — no superficial. Reconoce primero la dificultad.",
  contemplativo:
    "Responde contemplativamente. Prioriza resonancia espiritual sobre información.",
  reflexivo:
    "Profundidad sin agobio. Invita a pensar, no a resolver.",
  pastoral:
    null,  // default voice — no extra instruction needed
  exhortativo_suave:
    "Si conviene, ofrece una sola invitación suave al final, sin insistir ni repetir.",
  celebrativo:
    "Celebra con calidez genuina, sin exageración ni efusividad artificial.",
  practico:
    "Sé claro y ordenado. Sin relleno emocional cuando se pide información práctica.",
};

/**
 * @param {{
 *   emotion:   object|null,
 *   mood:      string,
 *   intent:    string,
 *   depth:     string,
 *   riskLevel: string,
 * }} params
 * @returns {{
 *   wisdomTone:  string,
 *   instruction: string|null,
 * }}
 */
export function shapeWisdomResponse({ emotion, mood, intent, depth, riskLevel }) {
  // Grief context → consolador
  if (mood === "grief" || emotion?.dominantEmotion === "grief") {
    return { wisdomTone: "consolador", instruction: WISDOM_TONE_INSTRUCTIONS.consolador };
  }

  // Discouragement, loneliness, fear → esperanzador
  if (["discouragement", "loneliness", "fear"].includes(emotion?.dominantEmotion)) {
    return { wisdomTone: "esperanzador", instruction: WISDOM_TONE_INSTRUCTIONS.esperanzador };
  }

  // Exhaustion → esperanzador with rest emphasis
  if (emotion?.dominantEmotion === "exhaustion") {
    return {
      wisdomTone: "esperanzador",
      instruction: "Responde con descanso pastoral. Menos instrucciones, más orientación hacia la calma.",
    };
  }

  // Celebration context
  if (mood === "celebration") {
    return { wisdomTone: "celebrativo", instruction: WISDOM_TONE_INSTRUCTIONS.celebrativo };
  }

  // Spiritual confusion → contemplativo (not explanatory)
  if (emotion?.dominantEmotion === "spiritual_confusion") {
    return { wisdomTone: "contemplativo", instruction: WISDOM_TONE_INSTRUCTIONS.contemplativo };
  }

  // Spiritual reflection
  if (intent === "reflection") {
    return { wisdomTone: "contemplativo", instruction: WISDOM_TONE_INSTRUCTIONS.contemplativo };
  }

  // Sermon preparation
  if (intent === "sermon") {
    return { wisdomTone: "reflexivo", instruction: WISDOM_TONE_INSTRUCTIONS.reflexivo };
  }

  // Planning / analysis → practico
  if (intent === "analysis" || intent === "planning") {
    return { wisdomTone: "practico", instruction: WISDOM_TONE_INSTRUCTIONS.practico };
  }

  // Pastoral followup without emotional signal → soft encouragement
  if (
    (intent === "family_followup" || intent === "visit_plan") &&
    !emotion?.detected &&
    riskLevel === "none"
  ) {
    return { wisdomTone: "exhortativo_suave", instruction: WISDOM_TONE_INSTRUCTIONS.exhortativo_suave };
  }

  return { wisdomTone: "pastoral", instruction: null };
}
